import { useEffect, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Page, PageTitleWithButton, Table, Pagination, Loader } from '../../../components';
import { Button, Theme, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Edit, Add } from '@mui/icons-material';
import { formatMoney } from '../../../helpers';
import { getAuthorities, getAuthority } from '../../../fetch';
import { IAuthority } from '../../../models';
import { MobileAuthority } from './MobileAuthority';
import { AuthorityModal } from './AuthorityModal';

export const ManageAuthorities = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [authorities, setAuthorities] = useState<IAuthority[]>([]);
  const [isLoadingAuthorities, setLoadingAuthorities] = useState<boolean>(true);
  const [isLoadingAuthority, setLoadingAuthority] = useState<boolean>(false);
  const [isModalOpen, showAuthoritiesModal] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [currentAuthority, setCurrentAuthority] = useState<any>(null);

  const isMobile = useMediaQuery('(max-width: 960px)');

  const classes = useStyles();

  const fetchAuthorities = async () => {
    try {
      setLoadingAuthorities(true);
      const res = await getAuthorities({ page: page + 1, perPage: perPage });
      setRecordCount(res.totalRecordCount);
      setAuthorities(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading authorities, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingAuthorities(false);
    }
  };

  const handleEdit = async id => {
    setLoadingAuthority(true);
    try {
      const res = await getAuthority(id);
      setCurrentAuthority(res);
      showAuthoritiesModal(true);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingAuthority(false);
    }
  };

  useEffect(() => {
    fetchAuthorities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Type',
        accessor: 'type',
        sort: false
      },
      {
        Header: 'Fee',
        accessor: 'fee',
        sort: false,
        Cell: ({ cell }) => {
          return <>{formatMoney(cell.row.original.fee, 2)}</>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IAuthority } };
        }) => {
          return (
            <>
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={async () => {
                  handleEdit(original.authorityId);
                }}
              >
                Edit
              </Button>
            </>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorities]);
  return (
    <>
      {isLoadingAuthority && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title="Manage Authorities">
        <PageTitleWithButton
          pageTitleBackProps={{
            title: 'Manage Authorities',
            showDivider: false,
            backText: 'Back to Admin home',
            backUrl: '/admin'
          }}
          button={
            <Button
              startIcon={<Add />}
              onClick={() => showAuthoritiesModal(true)}
              color="primary"
              variant="contained"
              className={classes.addButton}
            >
              Add New Authority
            </Button>
          }
        />
        <Table
          columns={columns}
          data={authorities}
          isLoading={isLoadingAuthorities}
          hidePagination
          mobileProps={{
            handleEdit: (val: IAuthority) => {
              handleEdit(val.authorityId);
            }
          }}
          ResponsiveComponent={isMobile ? MobileAuthority : undefined}
        />
        {!isLoadingAuthorities && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
        <AuthorityModal
          currentAuthority={currentAuthority}
          open={isModalOpen}
          onClose={() => {
            showAuthoritiesModal(false);
            setTimeout(() => {
              setCurrentAuthority(null);
            }, 500);
          }}
          fetchAuthorities={fetchAuthorities}
        />
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  addNeighborhoodWrapper: {
    marginBottom: theme.spacing(1)
  },
  addNeighborhoodButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },

  infoWrapper: {
    '@media (min-width: 768px)': {
      textAlign: 'right'
    }
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  },
  addWrapper: {
    marginBottom: theme.spacing(1)
  },
  addButton: {
    alignSelf: 'flex-start',
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  }
}));
